import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import {
  AmenityCategory,
  AMENITY_CATEGORY_LABELS,
} from "@postnatal-care/shared-types";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  useCreateAmenity,
  useUpdateAmenity,
  type AmenityMasterEntity,
} from "@/hooks/useBusinesses";
import {
  amenityIconLibrary,
  getAmenityIcon,
} from "@/components/AmenityPicker/iconMap";

interface AmenityEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  amenity?: AmenityMasterEntity | null;
}

// This is intentionally used only by the Edit flow. Create continues to
// generate its icon key automatically.
const ICON_KEYS = Object.keys(amenityIconLibrary).sort();

const formatIconLabel = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

export const AmenityEditDialog = ({
  open,
  onOpenChange,
  amenity,
}: AmenityEditDialogProps) => {
  const isEdit = !!amenity;
  const [name, setName] = useState("");
  const [nameMl, setNameMl] = useState("");
  const [category, setCategory] = useState<AmenityCategory>(
    AmenityCategory.GENERAL_SERVICES,
  );
  const [iconKey, setIconKey] = useState(ICON_KEYS[0] ?? "");
  const [sortOrder, setSortOrder] = useState<string>("");
  const [isActive, setIsActive] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [iconSearch, setIconSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useCreateAmenity();
  const update = useUpdateAmenity();
  const busy = create.isPending || update.isPending;

  useEffect(() => {
    if (!open) return;
    if (amenity) {
      setName(amenity.name);
      setNameMl(amenity.nameMalayalam ?? "");
      setCategory(amenity.category ?? AmenityCategory.GENERAL_SERVICES);
      setIconKey(amenity.iconKey);
      setSortOrder(String(amenity.sortOrder));
      setIsActive(amenity.isActive);
      setIsPremium(amenity.isPremium ?? false);
    } else {
      setName("");
      setNameMl("");
      setCategory(AmenityCategory.GENERAL_SERVICES);
      setIconKey(ICON_KEYS[0] ?? "");
      setSortOrder("");
      setIsActive(true);
      setIsPremium(false);
    }
    setIconSearch("");
    setError(null);
  }, [open, amenity]);

  const filteredIconKeys = useMemo(() => {
    const q = iconSearch.trim().toLowerCase().replace(/\s+/g, "_");
    return q ? ICON_KEYS.filter((k) => k.includes(q)) : ICON_KEYS;
  }, [iconSearch]);

  const suggestedKey = useMemo(() => (name ? slugify(name) : ""), [name]);
  const finalKey = iconKey || suggestedKey;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError("Name is required");
    if (isEdit && !finalKey) return setError("Pick an icon");
    const parsedSort = sortOrder === "" ? undefined : Number(sortOrder);
    if (
      parsedSort !== undefined &&
      (!Number.isInteger(parsedSort) || parsedSort < 0)
    ) {
      return setError("Sort order must be a non-negative integer");
    }
    try {
      if (isEdit && amenity) {
        await update.mutateAsync({
          id: amenity.id,
          data: {
            name: name.trim(),
            nameMalayalam: nameMl.trim() || undefined,
            category,
            iconKey: finalKey,
            sortOrder: parsedSort,
            isActive,
            isPremium,
          },
        });
        toast.success(`Updated "${name.trim()}"`);
      } else {
        await create.mutateAsync({
          name: name.trim(),
          nameMalayalam: nameMl.trim() || undefined,
          category,
          sortOrder: parsedSort,
          isPremium,
        });
        toast.success(`Added "${name.trim()}"`);
      }
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit amenity" : "New amenity"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Breakfast included"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label>Name (Malayalam)</Label>
            <Input
              value={nameMl}
              onChange={(e) => setNameMl(e.target.value)}
              placeholder="മലയാളം പേര്"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as AmenityCategory)}
              className="w-full h-10 px-3 py-2 text-sm rounded-md border border-input bg-background"
            >
              {Object.values(AmenityCategory).map((cat) => (
                <option key={cat} value={cat}>
                  {AMENITY_CATEGORY_LABELS[cat]?.en ?? cat}
                </option>
              ))}
            </select>
          </div>
          {isEdit && (
            <div className="space-y-1.5">
              <Label>Icon</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  value={iconSearch}
                  onChange={(e) => setIconSearch(e.target.value)}
                  placeholder="Search icons…"
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <div className="grid grid-cols-4 gap-1.5 max-h-52 overflow-y-auto rounded-md border p-2">
                {filteredIconKeys.length === 0 ? (
                  <p className="col-span-4 py-4 text-center text-xs text-muted-foreground">
                    No icons match "{iconSearch}"
                  </p>
                ) : (
                  filteredIconKeys.map((key) => {
                    const Icon = getAmenityIcon(key);
                    const selected = key === iconKey;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setIconKey(key)}
                        className={`flex flex-col items-center gap-1 p-2 rounded border transition-colors text-center ${
                          selected
                            ? "border-primary bg-primary/5 text-primary"
                            : "border-transparent hover:bg-muted"
                        }`}
                        aria-label={key}
                      >
                        <Icon className="w-5 h-5 shrink-0" />
                        <span className="text-[10px] leading-tight line-clamp-2 break-words w-full">
                          {formatIconLabel(key)}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Sort order</Label>
              <Input
                type="number"
                min={0}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                placeholder="auto"
              />
            </div>
            {isEdit && (
              <div className="space-y-1.5">
                <Label>Active</Label>
                <div className="flex items-center gap-2 h-9">
                  <Switch checked={isActive} onCheckedChange={setIsActive} />
                  <span className="text-sm text-muted-foreground">
                    {isActive ? "Visible" : "Hidden"}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2 py-2">
            <Switch
              id="editIsPremium"
              checked={isPremium}
              onCheckedChange={setIsPremium}
            />
            <Label htmlFor="editIsPremium">Is Premium / Unique?</Label>
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Saving…" : isEdit ? "Save changes" : "Create amenity"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AmenityEditDialog;
